import { useEffect, useState } from "react";
import Quill from "quill";
import { Pfeil } from "./Textwerk";

/*
  Die Werkzeugleiste über der Folienbühne.

  ---------------------------------------------------------------
  Warum sie oben steht und nicht am Text schwebt
  ---------------------------------------------------------------

  Quill bringt eine schwebende Leiste mit („bubble"), die erst erscheint,
  wenn man Text auswählt, und sich dann über die Stelle legt. Genau die war
  hier zuerst eingebaut — und sie ist für diese Zielgruppe die falsche Wahl:

    Man muss WISSEN, dass es sie gibt.  Wer nie Text auswählt, sieht nie
    eine Möglichkeit, etwas fett zu setzen.

    Sie verdeckt, was man gerade bearbeitet.  Auf einer Folie ist der Platz
    ohnehin knapp.

    Sie sitzt woanders als im Textdokument.  Zwei Bereiche desselben
    Programms, zwei Bedienweisen — das muss man sich zweimal merken.

  Deshalb steht sie jetzt dort, wo sie im Textdokument auch steht: oben,
  über die ganze Breite, immer sichtbar. Dieselben Klassen, dieselben
  Symbole, dieselbe Größe der Knöpfe.

  ---------------------------------------------------------------
  Warum sie von Hand gebaut ist und nicht von Quill
  ---------------------------------------------------------------

  Im Textdokument gibt es EIN Quill, und dem übergibt man die Leiste; Quill
  hängt seine Befehle selbst daran. Auf einer Folie gibt es aber je
  Textkasten ein eigenes Quill. Eine gemeinsame Leiste könnte man keinem
  davon übergeben — jedes würde seine Befehle an dieselben Knöpfe hängen,
  und ein Druck auf „Fett" wirkte in allen Kästen gleichzeitig.

  Also hängen die Knöpfe hier von Hand am zuletzt benutzten Kasten. Die
  Symbole holt sich die Leiste trotzdem von Quill, damit sie Strich für
  Strich dieselben sind wie im Textdokument.
*/

const SYMBOLE = Quill.import("ui/icons");

const AUSRICHTUNGEN = [
  { wert: "", titel: "Linksbündig" },
  { wert: "center", titel: "Zentriert" },
  { wert: "right", titel: "Rechtsbündig" },
];

const SCHRIFTGROESSEN = [
  { wert: "small", titel: "Klein" },
  { wert: "", titel: "Normal" },
  { wert: "large", titel: "Groß" },
  { wert: "huge", titel: "Sehr groß" },
];

export default function Folienleiste({
  quill,
  rueckgaengig,
  kannZurueck,
  kannVor,
  laedtBild,
  aufTextfeld,
  aufBild,
  aufVorfuehren,
  aufDrucken,
  kannKommentieren,
  aufKommentar,
}) {
  const [format, setzeFormat] = useState({});

  /*
    Was gerade gilt, damit die Knöpfe es anzeigen können. Ohne das wüsste
    niemand, ob die Schreibmarke in fettem Text steht.

    ---------------------------------------------------------------
    Warum hier die Stelle ausdrücklich mitgegeben wird
    ---------------------------------------------------------------

    `quill.getFormat()` OHNE Angabe einer Stelle ruft intern
    `getSelection(true)` — und das `true` heißt bei Quill: „hol dir dabei
    den Fokus". Ein Aufruf, der nur nachsehen soll, greift also nach der
    Tastatur.

    Das hat einen bösen Fehler gebaut: Wer einen Kommentar schreiben wollte,
    bekam nur einen Buchstaben ins Feld. Denn sobald das Notizfeld den Fokus
    nahm, meldete Quill „Auswahl weg", diese Zeile fragte das Format ab,
    Quill holte sich den Fokus zurück — und alles Weitere landete im
    Folienkasten statt im Kommentar. Ein Fokus-Pingpong, im Ereignisprotokoll
    gut zu sehen und auf dem Bildschirm überhaupt nicht.

    Deshalb: erst die Stelle holen (ohne Fokus), und nur wenn es eine gibt,
    danach fragen. Gibt es keine, ist auch nichts zu formatieren.
  */
  useEffect(() => {
    if (!quill) {
      setzeFormat({});
      return undefined;
    }
    const merke = () => {
      const bereich = quill.getSelection();
      setzeFormat(bereich ? quill.getFormat(bereich) : {});
    };
    merke();
    quill.on("selection-change", merke);
    quill.on("text-change", merke);
    return () => {
      quill.off("selection-change", merke);
      quill.off("text-change", merke);
    };
  }, [quill]);

  const aus = !quill;

  function setze(name, wert) {
    if (!quill) return;
    quill.focus();
    quill.format(name, wert, "user");
    setzeFormat(quill.getFormat());
  }

  /* Fett, Kursiv, Unterstrichen schalten um — zweimal drücken hebt auf. */
  function schalte(name) {
    setze(name, !format[name]);
  }

  return (
    <div className="werkzeugleiste nicht-drucken">
      <div className="werkzeugleiste__eigen">
        <button
          className="wkz"
          title="Rückgängig (Strg+Z)"
          aria-label="Rückgängig"
          disabled={!kannZurueck}
          onClick={() => rueckgaengig && rueckgaengig.undo()}
        >
          <Pfeil richtung="zurueck" />
        </button>
        <button
          className="wkz"
          title="Wiederholen (Strg+Umschalt+Z)"
          aria-label="Wiederholen"
          disabled={!kannVor}
          onClick={() => rueckgaengig && rueckgaengig.redo()}
        >
          <Pfeil richtung="vor" />
        </button>

        <span className="werkzeugleiste__trenner" />

        {/*
          Einfügen. Beide Knöpfe tragen ihr Wort, nicht nur ein Zeichen —
          „Textfeld" und „Bild" sind nicht mit einem Sinnbild zu treffen,
          das jeder auf Anhieb versteht, und Platz ist in der Leiste.
        */}
        <button className="wkz wkz--wort" onClick={aufTextfeld} title="Textfeld einfügen">
          Textfeld
        </button>
        <button
          className="wkz wkz--wort"
          onClick={aufBild}
          disabled={laedtBild}
          title="Bild einfügen"
        >
          {laedtBild ? "Lädt …" : "Bild"}
        </button>
        {/*
          Kommentieren ist auch ein Einfügen — nur eben einer Bemerkung
          statt eines Kastens. Gesperrt, solange nichts ausgewählt ist: Ein
          Kommentar braucht eine Stelle, an der er hängen kann, und eine
          Schreibmarke ohne Ausdehnung ist keine.

          `onMouseDown` mit `preventDefault`, sonst nähme der Knopf beim
          Drücken den Fokus und die Auswahl wäre weg, bevor der Kommentar
          sie bekommt.
        */}
        <button
          className="wkz wkz--wort"
          onMouseDown={(e) => e.preventDefault()}
          onClick={aufKommentar}
          disabled={!kannKommentieren}
          title="Kommentar zur ausgewählten Stelle"
        >
          Kommentar
        </button>

        <span className="werkzeugleiste__trenner" />

        {/*
          Vorführen und Drucken gehören der ganzen Präsentation, nicht dem
          gewählten Kasten — deshalb hier links bei den eigenen Knöpfen und
          nicht bei Quills Werkzeugen, die ohne Kasten gesperrt sind.
        */}
        <button className="wkz wkz--wort" onClick={aufVorfuehren} title="Vollbild, weiter mit den Pfeiltasten">
          Vorführen
        </button>
        <button className="wkz wkz--wort" onClick={aufDrucken} title="Eine Folie je Seite, quer">
          Drucken
        </button>

        <span className="werkzeugleiste__trenner" />

        <label className="wkz-zoom">
          <span className="nur-fuer-vorleser">Schriftgröße</span>
          <select
            value={format.size || ""}
            disabled={aus}
            onChange={(e) => setze("size", e.target.value || false)}
          >
            {SCHRIFTGROESSEN.map((s) => (
              <option key={s.wert} value={s.wert}>
                {s.titel}
              </option>
            ))}
          </select>
        </label>

        <span className="werkzeugleiste__trenner" />
      </div>

      {/*
        Ab hier Quills eigene Klassen und Symbole — damit die Leiste
        aussieht wie die im Textdokument. `ql-toolbar ql-snow` setzt sonst
        Quill selbst; hier steht es von Hand da, weil die Leiste keinem
        Quill gehört.
      */}
      <div className="werkzeugleiste__quill ql-toolbar ql-snow">
        <span className="ql-formats">
          <Werkzeug
            klasse="ql-bold"
            titel="Fett"
            zeichen={SYMBOLE.bold}
            an={!!format.bold}
            aus={aus}
            aufDruck={() => schalte("bold")}
          />
          <Werkzeug
            klasse="ql-italic"
            titel="Kursiv"
            zeichen={SYMBOLE.italic}
            an={!!format.italic}
            aus={aus}
            aufDruck={() => schalte("italic")}
          />
          <Werkzeug
            klasse="ql-underline"
            titel="Unterstrichen"
            zeichen={SYMBOLE.underline}
            an={!!format.underline}
            aus={aus}
            aufDruck={() => schalte("underline")}
          />
        </span>

        <span className="ql-formats">
          <Werkzeug
            klasse="ql-list"
            titel="Aufzählung"
            zeichen={SYMBOLE.list.bullet}
            an={format.list === "bullet"}
            aus={aus}
            aufDruck={() => setze("list", format.list === "bullet" ? false : "bullet")}
          />
          <Werkzeug
            klasse="ql-list"
            titel="Nummerierte Liste"
            zeichen={SYMBOLE.list.ordered}
            an={format.list === "ordered"}
            aus={aus}
            aufDruck={() => setze("list", format.list === "ordered" ? false : "ordered")}
          />
        </span>

        <span className="ql-formats">
          {AUSRICHTUNGEN.map((a) => (
            <Werkzeug
              key={a.wert || "links"}
              klasse="ql-align"
              titel={a.titel}
              zeichen={SYMBOLE.align[a.wert]}
              an={(format.align || "") === a.wert}
              aus={aus}
              aufDruck={() => setze("align", a.wert || false)}
            />
          ))}
        </span>

        <span className="ql-formats">
          <Werkzeug
            klasse="ql-clean"
            titel="Formatierung entfernen"
            zeichen={SYMBOLE.clean}
            an={false}
            aus={aus}
            aufDruck={() => {
              if (!quill) return;
              quill.focus();
              const bereich = quill.getSelection();
              if (bereich) quill.removeFormat(bereich.index, bereich.length, "user");
              setzeFormat(quill.getFormat());
            }}
          />
        </span>
      </div>
    </div>
  );
}

/*
  Ein Knopf der Leiste.

  `onMouseDown` mit `preventDefault` ist hier keine Feinheit, sondern
  Bedingung: Ohne das nimmt der Knopf beim Drücken den Fokus, die Auswahl im
  Text verschwindet — und „Fett" hätte nichts mehr, worauf es wirken könnte.
*/
function Werkzeug({ klasse, titel, zeichen, an, aus, aufDruck }) {
  return (
    <button
      type="button"
      className={`${klasse} ${an ? "ql-active" : ""}`}
      title={titel}
      aria-label={titel}
      aria-pressed={an}
      disabled={aus}
      onMouseDown={(e) => e.preventDefault()}
      onClick={aufDruck}
      dangerouslySetInnerHTML={{ __html: zeichen }}
    />
  );
}
